/* Everest VW product page (Get You Booked, Oct 2026).
   1. Install videos: copies the videos from the Instagram feed app at the bottom of the page into 9:16 cards
      (under the gallery on desktop, under the buy box on phones) and plays them in a full-screen popup.
      The feed app refreshes its video links on every visit, so the cards always show the latest posts.
      If the feed is removed or has no videos, the block stays hidden.
   2. Desktop: when the gallery column is taller than the window, it scrolls with the page until its bottom
      is in view and then sticks, so nothing in it is cut off. */
(function () {
  'use strict';

  var SOURCE = '.instafeed-new-layout-container video';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function collect() {
    var seen = {};
    var out = [];
    document.querySelectorAll(SOURCE).forEach(function (v) {
      if (v.closest('ev-reels') || v.closest('[data-reelpop]')) return;
      var source = v.querySelector('source');
      var src = v.getAttribute('src') || (source && source.getAttribute('src')) || '';
      if (!src || seen[src]) return;
      seen[src] = true;
      out.push({ src: src, poster: v.getAttribute('poster') || v.getAttribute('data-poster') || '' });
    });
    return out;
  }

  /* ---------- Desktop gallery column: stick once its bottom is visible ---------- */
  var root = document.querySelector('[data-evp]');
  function fitMedia() {
    if (!root) return;
    var media = root.querySelector('.evp__media');
    if (!media) return;
    if (window.innerWidth < 990) {
      root.style.removeProperty('--evp-media-top');
      return;
    }
    var sticky = parseFloat(getComputedStyle(root).getPropertyValue('--evp-sticky')) || 16;
    var top = Math.min(sticky, window.innerHeight - media.offsetHeight - 16);
    root.style.setProperty('--evp-media-top', Math.round(top) + 'px');
  }
  if (root) {
    var fitQueued = false;
    var fitLater = null;
    var queueFit = function () {
      if (!fitQueued) {
        fitQueued = true;
        requestAnimationFrame(function () { fitQueued = false; fitMedia(); });
      }
      clearTimeout(fitLater);
      fitLater = setTimeout(fitMedia, 200);
    };
    window.addEventListener('resize', queueFit);
    window.addEventListener('scroll', queueFit, { passive: true });
    window.addEventListener('load', fitMedia);
    document.addEventListener('evp:media-changed', queueFit);
    fitMedia();
  }

  if (window.customElements.get('ev-reels')) return;

  class EvReels extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.track = this.querySelector('[data-reels-track]');
      this.prev = this.querySelector('[data-reels-prev]');
      this.next = this.querySelector('[data-reels-next]');
      this.pop = document.querySelector('[data-reelpop]');
      if (!this.track) return;

      var self = this;
      var tryBuild = function () {
        var items = collect();
        if (!items.length) return false;
        self.build(items);
        return true;
      };
      if (tryBuild()) return;

      var queued = false;
      var observer = new MutationObserver(function () {
        if (queued) return;
        queued = true;
        setTimeout(function () {
          queued = false;
          if (tryBuild()) {
            observer.disconnect();
            clearTimeout(self.giveUp);
          }
        }, 80);
      });
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
      this.giveUp = setTimeout(function () { observer.disconnect(); }, 20000);
    }

    build(items) {
      var self = this;
      this.items = items;
      this.track.innerHTML = items.map(function (it, i) {
        return '<li class="evp-reels__item"><button type="button" class="evp-reel" data-i="' + i + '" aria-label="Play install video ' + (i + 1) + ' of ' + items.length + '">' +
          '<video muted playsinline loop preload="none"' + (it.poster ? ' poster="' + esc(it.poster) + '"' : '') + ' data-src="' + esc(it.src) + '"></video>' +
          '<span class="evp-reel__play" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 14 14"><path d="M3 1.5v11l9-5.5z" fill="currentColor"/></svg></span>' +
          '</button></li>';
      }).join('');
      this.hidden = false;
      this.cards = Array.prototype.slice.call(this.track.querySelectorAll('.evp-reel'));

      this.cards.forEach(function (card) {
        card.addEventListener('click', function () { self.open(+card.getAttribute('data-i')); });
      });

      if ('IntersectionObserver' in window && !reduceMotion) {
        this.io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            var v = en.target.querySelector('video');
            if (!v) return;
            if (en.isIntersecting && en.intersectionRatio >= 0.6 && !(self.pop && self.pop.open)) {
              if (!v.getAttribute('src')) v.setAttribute('src', v.getAttribute('data-src'));
              var p = v.play();
              if (p && p.catch) p.catch(function () {});
            } else {
              v.pause();
            }
          });
        }, { threshold: [0, 0.6, 1] });
        this.cards.forEach(function (c) { self.io.observe(c); });
      }

      var step = function (dir) {
        var first = self.track.querySelector('.evp-reels__item');
        var gap = parseFloat(getComputedStyle(self.track).columnGap) || 12;
        var w = first ? first.getBoundingClientRect().width + gap : self.track.clientWidth;
        self.track.scrollBy({ left: dir * w, behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      if (this.prev) this.prev.addEventListener('click', function () { step(-1); });
      if (this.next) this.next.addEventListener('click', function () { step(1); });
      var updateArrows = function () {
        var max = self.track.scrollWidth - self.track.clientWidth;
        var nav = self.querySelector('.evp-reels__nav');
        if (nav) nav.style.visibility = max > 4 ? '' : 'hidden';
        if (self.prev) self.prev.disabled = self.track.scrollLeft <= 4;
        if (self.next) self.next.disabled = self.track.scrollLeft >= max - 4;
      };
      this.track.addEventListener('scroll', function () { clearTimeout(self.arrowT); self.arrowT = setTimeout(updateArrows, 60); }, { passive: true });
      window.addEventListener('resize', updateArrows);
      updateArrows();

      this.bindPop();
      document.dispatchEvent(new CustomEvent('evp:media-changed'));
    }

    bindPop() {
      var self = this;
      var pop = this.pop;
      if (!pop || this.popBound) return;
      this.popBound = true;
      this.popVideo = pop.querySelector('[data-reelpop-video]');
      this.popCount = pop.querySelector('[data-reelpop-count]');
      this.popPrev = pop.querySelector('[data-reelpop-prev]');
      this.popNext = pop.querySelector('[data-reelpop-next]');

      pop.querySelector('[data-reelpop-close]').addEventListener('click', function () { self.close(); });
      if (this.popPrev) this.popPrev.addEventListener('click', function () { self.show(self.current - 1); });
      if (this.popNext) this.popNext.addEventListener('click', function () { self.show(self.current + 1); });
      pop.addEventListener('close', function () { self.cleanup(); });
      pop.addEventListener('cancel', function () { self.cleanup(); });
      pop.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); self.show(self.current - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); self.show(self.current + 1); }
      });
      pop.addEventListener('click', function (e) { if (e.target === pop) self.close(); });

      var startX = null;
      var startY = null;
      pop.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });
      pop.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        var dy = e.changedTouches[0].clientY - startY;
        startX = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) self.show(self.current + (dx < 0 ? 1 : -1));
        else if (dy > 90 && Math.abs(dy) > Math.abs(dx)) self.close();
      }, { passive: true });
    }

    open(i) {
      if (!this.pop || typeof this.pop.showModal !== 'function') {
        window.open(this.items[i].src, '_blank', 'noopener');
        return;
      }
      this.cards.forEach(function (c) { var v = c.querySelector('video'); if (v) v.pause(); });
      document.documentElement.style.overflow = 'hidden';
      this.pop.showModal();
      this.show(i);
    }

    show(i) {
      var n = this.items.length;
      this.current = (i + n) % n;
      var it = this.items[this.current];
      var v = this.popVideo;
      v.pause();
      if (it.poster) v.setAttribute('poster', it.poster); else v.removeAttribute('poster');
      v.setAttribute('src', it.src);
      v.muted = false;
      var p = v.play();
      if (p && p.catch) {
        p.catch(function () {
          v.muted = true;
          var q = v.play();
          if (q && q.catch) q.catch(function () {});
        });
      }
      if (this.popCount) this.popCount.textContent = (this.current + 1) + ' / ' + n;
      var single = n < 2;
      if (this.popPrev) this.popPrev.hidden = single;
      if (this.popNext) this.popNext.hidden = single;
    }

    close() {
      if (this.pop && this.pop.open) this.pop.close();
      this.cleanup();
    }

    cleanup() {
      document.documentElement.style.overflow = '';
      if (this.popVideo) {
        this.popVideo.pause();
        this.popVideo.removeAttribute('src');
        this.popVideo.load();
      }
    }
  }

  window.customElements.define('ev-reels', EvReels);
})();
